/**
 * What the signed-in administrator may do.
 *
 * Roles are gone: an account is its grants, and the console asks what
 * someone may do rather than what they are called.
 */
export const Feature = {
  PARTICIPANTS: 'participants',
  // Marking someone present, and nothing else.
  CHECK_IN: 'check_in',
  EDIT_PARTICIPANT: 'edit_participant',
  // Taking money at the desk. Not part of check-in: marking people present
  // is not the same trust as handling cash.
  PAYMENTS: 'payments',
  ACCOMODATION: 'accomodation',
  RESOURCE_GROUPS: 'resource_groups',
  PARTICIPANT_REPORTS: 'participant_reports',
  CENTER_REPORTS: 'center_reports',
  EVENTS: 'events',
  SESSIONS: 'sessions',
  USERS: 'users',
  // The short links behind tarm.cc and how often they are followed. Its
  // own grant rather than part of events: setting up an event needs a
  // link for it; asking which flyer worked needs the counts across all of
  // them, including events somebody had no hand in.
  SHORT_LINKS: 'short_links',
  // Handing follow-up work out and recording it — its own grant, so a
  // volunteer logging calls needs nothing else.
  FOLLOW_UPS: 'follow_ups',
  // Setting a centre up: its words, sub-centres and the events they run.
  CENTERS: 'centers',
  // The visitation team's grant: newcomers' contacts and their visits.
  VISITATION: 'visitation',
}

/**
 * What each feature is called on screen.
 *
 * Lives beside the enum rather than in the page that renders it. There
 * were two hand-kept copies of this, and adding a feature to the enum
 * without touching both meant the grant existed, worked, and could not be
 * given to anybody — which is exactly what happened to short_links.
 */
export const FEATURE_LABELS = {
  [Feature.CHECK_IN]: 'Checking people in',
  [Feature.EDIT_PARTICIPANT]: 'Correcting participant details',
  [Feature.PAYMENTS]: 'Taking payment at the desk',
  [Feature.PARTICIPANTS]: 'Participant records',
  [Feature.ACCOMODATION]: 'Accommodation',
  [Feature.RESOURCE_GROUPS]: 'Resource groups',
  [Feature.PARTICIPANT_REPORTS]: 'Participant reports',
  [Feature.CENTER_REPORTS]: 'Centre reports',
  [Feature.EVENTS]: 'Events',
  [Feature.SESSIONS]: 'Sessions and check-in',
  [Feature.USERS]: 'Admin accounts',
  [Feature.SHORT_LINKS]: 'Short links',
  [Feature.FOLLOW_UPS]: 'Follow-ups and assignments',
  [Feature.CENTERS]: 'Centre setup (in the centre portal)',
  [Feature.VISITATION]: 'Newcomer visitation',
}

/**
 * Every feature, in a stable order, with a label.
 *
 * Derived from the enum rather than from the label map, so a feature
 * added to one and not the other still appears — unlabelled and obvious,
 * rather than absent and silent.
 */
export const allFeatures = () =>
  Object.values(Feature).map((feature) => ({
    feature,
    label: FEATURE_LABELS[feature] ?? feature.replace(/_/g, ' '),
  }))

export const Action = {
  VIEW: 'view',
  MANAGE: 'manage',
}

const STORAGE_KEY = 'permissions'

export const savePermissions = (permissions) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions || []))
}

export const getPermissions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const stored = raw ? JSON.parse(raw) : null
    if (Array.isArray(stored) && stored.length) return stored
  } catch {
    // Nothing stored, or unreadable.
  }
  // Nothing rather than a guess: `refreshPermissions` fetches the real
  // list on load.
  return []
}

/**
 * Pull the signed-in account's current grants from the API.
 *
 * Called on load so a session that predates grants — or one whose access
 * was changed while it was open — is corrected without needing to sign out.
 */
export const refreshPermissions = async (axiosInstance, baseUrl, config) => {
  try {
    const resp = await axiosInstance.get(`${baseUrl}/admins/me`, config)
    const permissions = resp?.data?.data?.permissions
    if (Array.isArray(permissions)) savePermissions(permissions)
    return permissions
  } catch {
    return null
  }
}

export const clearPermissions = () => localStorage.removeItem(STORAGE_KEY)

/** `manage` implies `view`, matching the rule the API applies. */
export const can = (feature, action = Action.VIEW) => {
  const granted = getPermissions()
  if (!granted.length) return false
  if (granted.includes(`${feature}:${Action.MANAGE}`)) return true
  return action === Action.VIEW && granted.includes(`${feature}:${Action.VIEW}`)
}

export default {
  Feature,
  Action,
  can,
  getPermissions,
  savePermissions,
  clearPermissions,
  refreshPermissions,
}
